import { defineStore } from "pinia";
import { computed } from "vue";
import { ref } from "vue";
import { useRouter } from "vue-router";
import cookie from "js-cookie";
import ymGoal from "@/helpers/goal.js";
import { gtmSendEvent } from "@/helpers/gtm.js";
import { fbTrackBookingEvent } from "@/helpers/fbEvents.js";
import { pushLeadBooked } from "@/helpers/leadBooked.js";
import { trackBookingCreated } from "@/helpers/siteEvents";
import api from "@/api";
import { useCreateClient } from "../composables/useCreateClient";
import { watchOnce } from "@vueuse/core";
import { useAdmitAd } from "@/stores/admitAdStore.js";
import { getSegmentId } from "@/helpers/segment/getSegmentId";
import { getQuizSessionId } from "@/helpers/quizSession/getQuizSessionId";
import { addOneHour } from "@/helpers/addOneHour";
import { getCustomTags } from "@/helpers/customTags";

export const useBookingFlow = defineStore(
  "bookingFlow",
  () => {
    const router = useRouter();
    const admitAdStore = useAdmitAd();

    const {
      isIdle: isClientIdle,
      isLoading: isClientLoading,
      isSuccess: isClientSuccess,
    } = useCreateClient();

    const isBookingLoading = ref(false);
    // Session storage
    const timeslotFirst = ref(false);

    // Parent info
    const clientId = ref(null);
    const clientPhone = ref(null);
    const noParentInfo = computed(() => {
      return !clientId.value || !clientPhone.value;
    });
    // Selected slot & child info
    const selectedTimezone = ref(null);
    const childName = ref(null);
    const selectedAge = ref(null);
    const selectSlot = ref(null);
    const busySlots = ref(null);

    const noSlotChildInfo = computed(() => {
      return (
        !selectedTimezone.value ||
        !childName.value ||
        !selectedAge.value ||
        !selectSlot.value ||
        !busySlots.value
      );
    });

    function init() {
      const url = new URL(document.location.href);
      if (!!url.searchParams.get("timeslots_first")) timeslotFirst.value = true;
    }

    function firstStep() {
      if (timeslotFirst.value) goBooking();
      else goIndex();
    }

    function goIndex() {
      router.replace({ name: "Index" });
    }

    function goBooking() {
      router.replace({ name: "Booking" });
    }

    async function nextStep() {
      // If we did't run creating client
      if (noParentInfo.value && isClientIdle.value) {
        goIndex();
        return;
      }
      if (noSlotChildInfo.value) {
        goBooking();
        return;
      }
      // If we did't get response from client API
      if (isClientLoading.value) {
        watchOnce(isClientSuccess, () => {
          nextStep();
        });
        return;
      }

      if (!isBookingLoading.value) await doBooking();
    }

    function prepareDistributionTags() {
      const orignalArr = JSON.parse(cookie.get("distribution_tag_ids") || "[]");

      //
      const url = new URL(document.location.href);
      const ids = url.searchParams.get("distribution_tag_ids");
      var decodedString = decodeURIComponent(ids);

      if (decodedString !== null) {
        var numberArray = decodedString.split(",").map(Number);
        // Convert the numberArray to an array of integers
        var integerArray = numberArray.map(function (num) {
          const parsedNum = parseInt(num, 10);
          if (!isNaN(parsedNum)) {
            return parsedNum;
          } else {
            return 0;
          }
        });
      }

      const uniques = [...new Set(orignalArr.concat(integerArray))];

      return uniques;
    }

    async function doBooking() {
      isBookingLoading.value = true;

      const tags = prepareDistributionTags();

      const segment = getSegmentId();
      const session_id = getQuizSessionId();

      let object = {
        segment: +segment,
        session_id,
        tz_offset: selectedTimezone.value.tz,
        kid_first_name: childName.value,
        kid_age: Number(selectedAge.value),
        client: cookie.get("client_id"),
        promocode: cookie.get("client_promocode"),
        fb_pixel: cookie.get("fb_pixel"),
        mk_starts_at: selectSlot.value.start.replace(" ", "T"),
        mk_ends_at: addOneHour(selectSlot.value.start).replace(" ", "T"),
        client_time: `${selectSlot.value.start}:00.000${selectedTimezone.value.utc}`,
        client_timezone_text: selectedTimezone.value.name,
        speed_internet: +cookie.get("speed_internet"),
        gclid: cookie.get("gclid"),
        redtrack_click_id: cookie.get("rtkclickid-store"),
        distribution_tag_ids: tags,
        landing_id: cookie.get("landing_id"),
        segment_timezone_text: cookie.get("segment_timezone_name") || "",
        landing_type: cookie.get("landing_type"),
        landing_type_referrer: cookie.get("landing_type_referrer"),
        acquisition_source: cookie.get("acquisition_source"),
        ref_code: cookie.get("ref_code")
      };
      if (selectSlot.value.is_fake) {
        object.is_fake_slot = true;
      }
      if (cookie.get("quiz_id")) {
        object.quiz_id = cookie.get("quiz_id");
      }
      const customTags = getCustomTags();
      if (customTags.length) object.custom_tags = customTags;
      if (localStorage.getItem("gift_percent")) {
        object.additional_info = localStorage.getItem("gift_percent");
      }

      const newBooking = await api.newBookingV2(object);
      setTimeout(() => {
        isBookingLoading.value = false;
      });
      if (!!newBooking) {
        // gtagNew("event", "conversion", {
        //   send_to: "AW-11031098938/sZmlCMfop4YYELrshIwp",
        // });
        localStorage.setItem("choisen_timeslots", JSON.stringify(busySlots.value));
        fbTrackBookingEvent("Lead", newBooking);
        ymGoal("test_class_succesfully_booked");
        gtmSendEvent("ab_page_view_trial_class_succesfully_booked");
        pushLeadBooked(newBooking);
        trackBookingCreated(newBooking, object);
        window.parent.postMessage(
          { event: "ab_page_view_trial_class_succesfully_booked" }, "*"
        );
        let utmListCookie = cookie.get("utmList");
        if (utmListCookie) {
            let utmListValue = JSON.parse(decodeURIComponent(utmListCookie));
                if (utmListValue.utm_source === "ld") {
                  gtmSendEvent("ab_page_view_trial_class_succesfully_booked_ld");
            }
          }  
        cookie.set("client_tz", selectedTimezone.value.tz);
        admitAdStore.sendFormDataAdmitAd(newBooking);
        resetInfo();
        router.replace(`/detail/${newBooking}`);
      } else {
        setTimeout(() => {
          router.go();
        }, 1000);
      }
    }

    function resetInfo() {
      clientId.value = null;
      clientPhone.value = null;
      selectedTimezone.value = null;
      childName.value = null;
      selectedAge.value = null;
      selectSlot.value = null;
      busySlots.value = null;
    }

    function setParentInfo({ client_id, client_phone }) {
      clientId.value = client_id;
      clientPhone.value = client_phone;
    }

    function setSlotChildInfo({ timezone, name, age, slot, slotsBusy }) {
      selectedTimezone.value = timezone;
      childName.value = name;
      selectedAge.value = age;
      selectSlot.value = slot;
      busySlots.value = slotsBusy;
    }

    return {
      init,
      firstStep,
      nextStep,
      setParentInfo,
      setSlotChildInfo,
      timeslotFirst,
      isBookingLoading,
      prepareDistributionTags,
    };
  },
  {
    persist: {
      key: "autobooking_flow",
      storage: sessionStorage,
      paths: ["timeslotFirst"],
    },
  }
);
